"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { ArrowLeft, X, Plus } from "lucide-react"
import { productsService, categoriesService } from "../../services/firebase"
import { deleteField } from "firebase/firestore"
import type { Product } from "../../store/slices/productsSlice"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import toast from "react-hot-toast"
import type { VariantColor, VariantSize, ProductVariant } from "../../types/variants"
import { slugify, generateMatrixVariants, computeVariantId } from "../../utils/variants"

interface Category {
  id: string
  name: string
  slug: string
  isActive: boolean
  tags?: string[]
}

interface WardrobeVariant {
  id: string
  name: string
  price: number
  isActive: boolean
}

interface ProductFormData {
  name: string
  price: number
  originalPrice?: number
  description: string
  categories: string[]
  stock: number
  material?: string
  warranty?: string
  isFeatured: boolean
  isNewArrival: boolean
  isBestSeller: boolean
  dimensions?: {
    length: number
    width: number
    height: number
  }
  // Wardrobe-specific fields
  enableWardrobeVariants?: boolean
  enableCustomSize?: boolean
  customSizeMultiplier?: number
}

const PREDEFINED_WARDROBE_SIZES = [
  { name: "Small", defaultPrice: 15000 },
  { name: "Medium", defaultPrice: 20000 },
  { name: "Large", defaultPrice: 25000 },
  { name: "Extra Large", defaultPrice: 30000 },
]

const EditProduct: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingProduct, setIsLoadingProduct] = useState(true)
  const [isLoadingCategories, setIsLoadingCategories] = useState(true)
  const [product, setProduct] = useState<
    (Product & { 
      variants?: ProductVariant[]
      variantColors?: VariantColor[]
      variantSizes?: VariantSize[]
      wardrobeVariants?: WardrobeVariant[]
      enableWardrobeVariants?: boolean
      enableCustomSize?: boolean
      customSizeMultiplier?: number
    }) | null
  >(null)

  const [images, setImages] = useState<string[]>([])
  const [features, setFeatures] = useState<string[]>([""])
  const [tags, setTags] = useState<string[]>([""])

  const [allCategories, setAllCategories] = useState<Category[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  // Regular variants
  const [enableVariants, setEnableVariants] = useState(false)
  const [variantSizes, setVariantSizes] = useState<VariantSize[]>([])
  const [variantColors, setVariantColors] = useState<VariantColor[]>([])
  const [variants, setVariants] = useState<ProductVariant[]>([])

  // Wardrobe variants
  const [enableWardrobeVariants, setEnableWardrobeVariants] = useState(false)
  const [enableCustomSize, setEnableCustomSize] = useState(false)
  const [customSizeMultiplier, setCustomSizeMultiplier] = useState(1250)
  const [wardrobeVariants, setWardrobeVariants] = useState<WardrobeVariant[]>([])

  // Add a local state to hold per-color new image link inputs
  const [colorImageInputs, setColorImageInputs] = useState<Record<number, string>>({})

  const [newImageUrl, setNewImageUrl] = useState<string>("")

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ProductFormData>()

  useEffect(() => {
    loadCategories()
    if (id) {
      loadProduct()
    }
  }, [id])

  useEffect(() => {
    if (enableVariants) {
      const combo = generateMatrixVariants(
        variantSizes,
        variantColors.map((c) => ({ name: c.name, hex: c.hex })),
        variants,
      ) as ProductVariant[]
      setVariants(combo.map((v) => ({ ...v, id: computeVariantId(v.size, v.color) })))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableVariants, variantSizes, variantColors])

  // Initialize wardrobe variants when enabled
  useEffect(() => {
    if (enableWardrobeVariants && wardrobeVariants.length === 0) {
      setWardrobeVariants(
        PREDEFINED_WARDROBE_SIZES.map((size, index) => ({
          id: `wardrobe-${index}`,
          name: size.name,
          price: size.defaultPrice,
          isActive: true,
        }))
      )
    }
  }, [enableWardrobeVariants])

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true)
      const categories = await categoriesService.getCategories()
      const activeCategories = categories
        .filter((cat: any) => cat.isActive)
        .map((cat: any) => ({
          ...cat,
          slug:
            cat.slug ||
            cat.name
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^a-z0-9-]/g, ""),
          tags: cat.tags || [],
        }))
      setAllCategories(activeCategories)
    } catch (error) {
      console.error("Error loading categories:", error)
      toast.error("Failed to load categories")
    } finally {
      setIsLoadingCategories(false)
    }
  }

  const loadProduct = async () => {
    try {
      setIsLoadingProduct(true)
      const productData = await productsService.getProduct(id!)
      if (productData) {
        setProduct(productData as any)
        setImages(productData.images || [])
        setFeatures(productData.features && productData.features.length > 0 ? productData.features : [""])
        setTags(productData.tags && productData.tags.length > 0 ? productData.tags : [""])

        const productCategories = (productData as any).categories || []
        setSelectedCategories(productCategories)

        reset({
          name: productData.name || "",
          price: productData.price || 0,
          originalPrice: productData.originalPrice || undefined,
          description: productData.description || "",
          categories: productCategories,
          stock: productData.stock || 0,
          material: productData.material || "",
          warranty: productData.warranty || "",
          isFeatured: productData.isFeatured || false,
          isNewArrival: productData.isNewArrival || false,
          isBestSeller: productData.isBestSeller || false,
          dimensions: productData.dimensions || undefined,
          enableWardrobeVariants: (productData as any).enableWardrobeVariants || false,
          enableCustomSize: (productData as any).enableCustomSize || false,
          customSizeMultiplier: (productData as any).customSizeMultiplier || 1250,
        })

        // Regular variants setup
        const vc = Array.isArray(productData.variantColors) ? (productData.variantColors as VariantColor[]) : []
        const vs = Array.isArray(productData.variantSizes) ? (productData.variantSizes as VariantSize[]) : []
        const v = Array.isArray(productData.variants) ? (productData.variants as ProductVariant[]) : []

        setVariantColors(vc)
        setVariantSizes(vs)
        setVariants(v.map((vv) => ({ ...vv, id: vv.id || computeVariantId(vv.size, vv.color) })))
        setEnableVariants(v.length > 0 || vc.length > 0 || vs.length > 0)

        // Wardrobe variants setup
        setEnableWardrobeVariants((productData as any).enableWardrobeVariants || false)
        setEnableCustomSize((productData as any).enableCustomSize || false)
        setCustomSizeMultiplier((productData as any).customSizeMultiplier || 1250)
        
        if ((productData as any).wardrobeVariants && Array.isArray((productData as any).wardrobeVariants)) {
          setWardrobeVariants((productData as any).wardrobeVariants)
        }
      } else {
        toast.error("Product not found")
        navigate("/admin/products")
      }
    } catch (error) {
      console.error("Error loading product:", error)
      toast.error("Failed to load product")
      navigate("/admin/products")
    } finally {
      setIsLoadingProduct(false)
    }
  }

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index))
  }

  // Validate URL
  const isValidUrl = (s: string) => {
    try {
      const u = new URL(s)
      return u.protocol === "http:" || u.protocol === "https:"
    } catch {
      return false
    }
  }

  // Add image by URL
  const handleAddImageUrl = () => {
    const url = newImageUrl.trim()
    if (!url) {
      toast.error("Enter an image URL")
      return
    }
    if (!isValidUrl(url)) {
      toast.error("Please enter a valid http(s) image URL")
      return
    }
    if (images.length >= 5) {
      toast.error("Maximum 5 images allowed")
      return
    }
    setImages((prev) => [...prev, url])
    setNewImageUrl("")
    toast.success("Image added")
  }

  const addFeature = () => setFeatures((prev) => [...prev, ""])
  const updateFeature = (index: number, value: string) =>
    setFeatures((prev) => prev.map((f, i) => (i === index ? value : f)))
  const removeFeature = (index: number) => setFeatures((prev) => prev.filter((_, i) => i !== index))

  const addTag = () => setTags((prev) => [...prev, ""])
  const updateTag = (index: number, value: string) => setTags((prev) => prev.map((t, i) => (i === index ? value : t)))
  const removeTag = (index: number) => setTags((prev) => prev.filter((_, i) => i !== index))

  const handleCategoryToggle = (categorySlug: string) => {
    setSelectedCategories((prev) => {
      const newSelection = prev.includes(categorySlug)
        ? prev.filter((slug) => slug !== categorySlug)
        : [...prev, categorySlug]
      setValue("categories", newSelection)
      return newSelection
    })
  }

  // Regular variant handlers
  const addSize = () => setVariantSizes((prev) => [...prev, { label: "" }])
  const updateSizeLabel = (index: number, value: string) =>
    setVariantSizes((prev) => prev.map((s, i) => (i === index ? { ...s, label: value } : s)))
  const removeSize = (index: number) => setVariantSizes((prev) => prev.filter((_, i) => i !== index))
  const toggleCustomDimensions = (index: number) => {
    setVariantSizes((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, dimensions: s.dimensions ? undefined : { length: undefined, width: undefined, height: undefined } }
          : s,
      ),
    )
  }
  const updateSizeDimension = (index: number, key: "length" | "width" | "height", value: number | undefined) =>
    setVariantSizes((prev) =>
      prev.map((s, i) => (i === index ? { ...s, dimensions: { ...(s.dimensions || {}), [key]: value } } : s)),
    )

  const addColor = () => setVariantColors((prev) => [...prev, { name: "", hex: "#cccccc", images: [] }])
  const updateColor = (idx: number, updates: Partial<VariantColor>) =>
    setVariantColors((prev) => prev.map((c, i) => (i === idx ? { ...c, ...updates } : c)))
  const removeColor = (idx: number) => setVariantColors((prev) => prev.filter((_, i) => i !== idx))

  function addColorImageUrl(colorIdx: number) {
    const url = (colorImageInputs[colorIdx] || "").trim()
    if (!url) {
      toast.error("Enter an image URL")
      return
    }
    if (!isValidUrl(url)) {
      toast.error("Please enter a valid http(s) image URL")
      return
    }
    setVariantColors((prev) => prev.map((c, i) => (i === colorIdx ? { ...c, images: [...(c.images || []), url] } : c)))
    setColorImageInputs((prev) => ({ ...prev, [colorIdx]: "" }))
    toast.success("Image URL added")
  }

  function updateColorImageUrl(colorIdx: number, imgIdx: number, url: string) {
    setVariantColors((prev) =>
      prev.map((c, i) =>
        i === colorIdx ? { ...c, images: (c.images || []).map((u, ii) => (ii === imgIdx ? url : u)) } : c,
      ),
    )
  }

  function removeColorImageUrl(colorIdx: number, imgIdx: number) {
    setVariantColors((prev) =>
      prev.map((c, i) => (i === colorIdx ? { ...c, images: (c.images || []).filter((_, ii) => ii !== imgIdx) } : c)),
    )
  }

  const updateVariantField = (variantId: string, field: string, value: any) => {
    setVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, [field]: value } : v)))
  }

  // Wardrobe variant handlers
  const addWardrobeVariant = () => {
    const newVariant: WardrobeVariant = {
      id: `wardrobe-${Date.now()}`,
      name: "",
      price: 0,
      isActive: true,
    }
    setWardrobeVariants((prev) => [...prev, newVariant])
  }

  const updateWardrobeVariant = (index: number, updates: Partial<WardrobeVariant>) => {
    setWardrobeVariants((prev) =>
      prev.map((variant, i) => (i === index ? { ...variant, ...updates } : variant))
    )
  }

  const removeWardrobeVariant = (index: number) => {
    setWardrobeVariants((prev) => prev.filter((_, i) => i !== index))
  }

  const onSubmit = async (data: ProductFormData) => {
    if (images.length === 0) {
      toast.error("Please add at least one product image")
      return
    }
    if (selectedCategories.length === 0) {
      toast.error("Please select at least one category")
      return
    }

    // Validation for wardrobe variants
    if (enableWardrobeVariants) {
      const validWardrobeVariants = wardrobeVariants.filter(v => v.name.trim() && v.price > 0)
      if (validWardrobeVariants.length === 0) {
        toast.error("Please add at least one valid wardrobe variant with name and price")
        return
      }
    }

    setIsLoading(true)
    try {
      const categoryNames = selectedCategories.map((catSlug) => {
        const category = allCategories.find((cat) => cat.slug === catSlug)
        return category?.name || catSlug
      })

      // Regular variants prep
      let preparedVariants: ProductVariant[] | undefined
      let preparedVariantColors: VariantColor[] | undefined
      let preparedVariantSizes: VariantSize[] | undefined
      let totalVariantStock = 0

      if (enableVariants) {
        const cleanedSizes = variantSizes.filter((s) => s.label && s.label.trim().length > 0)
        const cleanedColors = variantColors
          .filter((c) => c.name && c.name.trim().length > 0 && /^#([0-9A-F]{3}){1,2}$/i.test(c.hex || "#ccc"))
          .map((c) => ({ ...c, slug: slugify(c.name) }))

        preparedVariantSizes = cleanedSizes
        preparedVariantColors = cleanedColors

        const matrix = generateMatrixVariants(
          cleanedSizes,
          cleanedColors.map((c) => ({ name: c.name, hex: c.hex })),
          variants,
        ) as ProductVariant[]

        preparedVariants = matrix.map((v) => ({
          ...v,
          id: computeVariantId(v.size, v.color),
          stock: Number(v.stock || 0),
          priceModifier: Number(v.priceModifier || 0),
          isActive: v.isActive !== false,
        }))

        totalVariantStock = preparedVariants.reduce((sum, v) => sum + (v.stock || 0), 0)
      }

      // Wardrobe variants prep
      let preparedWardrobeVariants: WardrobeVariant[] | undefined
      if (enableWardrobeVariants) {
        preparedWardrobeVariants = wardrobeVariants
          .filter(v => v.name.trim() && v.price > 0)
          .map(v => ({
            ...v,
            name: v.name.trim(),
            price: Number(v.price),
            isActive: v.isActive !== false,
          }))
      }

      const productData: any = {
        ...data,
        images: images,
        features: features.filter((f) => f.trim() !== ""),
        tags: tags.filter((t) => t.trim() !== ""),
        categories: selectedCategories,
        categoryNames: categoryNames,
        categoryPath: selectedCategories.length > 0 ? `/categories/${selectedCategories[0]}` : "",
        updatedAt: new Date().toISOString(),
      }

      // Handle regular variants
      if (enableVariants && preparedVariants && preparedVariants.length > 0) {
        productData.variants = preparedVariants
        productData.variantColors = preparedVariantColors
        productData.variantSizes = preparedVariantSizes
        productData.stock = totalVariantStock
      } else {
        productData.variants = deleteField()
        productData.variantColors = deleteField()
        productData.variantSizes = deleteField()
        productData.stock = Number(data.stock || 0)
      }

      // Handle wardrobe variants
      if (enableWardrobeVariants && preparedWardrobeVariants && preparedWardrobeVariants.length > 0) {
        productData.wardrobeVariants = preparedWardrobeVariants
        productData.enableWardrobeVariants = true
        productData.enableCustomSize = enableCustomSize
        productData.customSizeMultiplier = Number(customSizeMultiplier)
      } else {
        productData.wardrobeVariants = deleteField()
        productData.enableWardrobeVariants = deleteField()
        productData.enableCustomSize = deleteField()
        productData.customSizeMultiplier = deleteField()
      }

      await productsService.updateProduct(id!, productData)

      toast.success("Product updated successfully!")
      navigate("/admin/products")
    } catch (error) {
      console.error("Error updating product:", error)
      toast.error("Failed to update product")
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingProduct || isLoadingCategories) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <button
            onClick={() => navigate("/admin/products")}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button onClick={() => navigate("/admin/products")} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Product</h1>
          <p className="text-gray-600">Update product information</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                  <input
                    {...register("name", { required: "Product name is required" })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter product name"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Base Price *</label>
                  <input
                    {...register("price", { required: "Price is required", min: 1 })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter base price"
                  />
                  {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>}
                  {enableWardrobeVariants && (
                    <p className="text-xs text-gray-500 mt-1">This will be overridden by variant prices</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Original Price</label>
                  <input
                    {...register("originalPrice", { min: 1 })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter original price (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity *</label>
                  <input
                    {...register("stock", { required: "Stock quantity is required", min: 0 })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter stock quantity"
                    disabled={(enableVariants && variants.length > 0) || enableWardrobeVariants}
                  />
                  {((enableVariants && variants.length > 0) || enableWardrobeVariants) && (
                    <p className="text-xs text-gray-500 mt-1">Stock is managed per variant</p>
                  )}
                  {errors.stock && <p className="mt-1 text-sm text-red-600">{errors.stock.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Material</label>
                  <input
                    {...register("material")}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Solid Wood, Metal"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Warranty</label>
                  <input
                    {...register("warranty")}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., 2 years"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    {...register("description", { required: "Description is required" })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter product description"
                  />
                  {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>}
                </div>
              </div>
            </div>

            {/* Categories Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories *</h3>
              <p className="text-sm text-gray-600 mb-4">
                Select one or more categories that best describe your product
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {allCategories.map((category) => {
                  const isSelected = selectedCategories.includes(category.slug)
                  return (
                    <div
                      key={category.id}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleCategoryToggle(category.slug)
                      }}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm select-none ${
                        isSelected
                          ? "border-primary-500 bg-primary-50 text-primary-700 shadow-sm"
                          : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
                          isSelected ? "border-primary-500 bg-primary-500" : "border-gray-300"
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium break-words">{category.name}</span>
                    </div>
                  )
                })}
              </div>
              {selectedCategories.length === 0 && (
                <p className="mt-2 text-sm text-red-600">Please select at least one category</p>
              )}
            </div>

            {/* Dimensions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dimensions (in)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Length</label>
                  <input
                    {...register("dimensions.length", { min: 0 })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Length"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Width</label>
                  <input
                    {...register("dimensions.width", { min: 0 })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Width"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                  <input
                    {...register("dimensions.height", { min: 0 })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Height"
                  />
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Features</h3>
                <button type="button" onClick={addFeature} className="text-primary-600 hover:text-primary-700">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter feature"
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
                <button type="button" onClick={addTag} className="text-primary-600 hover:text-primary-700">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                {tags.map((tag, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={tag}
                      onChange={(e) => updateTag(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter tag"
                    />
                    <button type="button" onClick={() => removeTag(index)} className="text-red-600 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Wardrobe Variants (New) */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Wardrobe Variants</h3>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enableWardrobeVariants}
                    onChange={(e) => {
                      setEnableWardrobeVariants(e.target.checked)
                      if (e.target.checked && enableVariants) {
                        setEnableVariants(false)
                        toast("Regular variants disabled - only one variant type allowed")
                      }
                    }}
                    className="accent-primary-600"
                  />
                  Enable Wardrobe Variants
                </label>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Use wardrobe variants for predefined sizes with specific pricing and custom size options.
              </p>

              {enableWardrobeVariants && (
                <div className="mt-4 space-y-6">
                  {/* Custom Size Settings */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Custom Size Option</h4>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={enableCustomSize}
                          onChange={(e) => setEnableCustomSize(e.target.checked)}
                          className="accent-primary-600"
                        />
                        Enable Custom Size
                      </label>
                    </div>
                    
                    {enableCustomSize && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Size Price Multiplier (₹ per sq inch)
                        </label>
                        <input
                          type="number"
                          value={customSizeMultiplier}
                          onChange={(e) => setCustomSizeMultiplier(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="1250"
                          min="1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Price will be calculated as: Height × Width × {customSizeMultiplier}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Predefined Wardrobe Variants */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Predefined Sizes</h4>
                      <button
                        type="button"
                        onClick={addWardrobeVariant}
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        Add Size
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {wardrobeVariants.map((variant, index) => (
                        <div key={variant.id} className="flex items-center gap-4 p-3 border rounded-lg">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={variant.name}
                              onChange={(e) => updateWardrobeVariant(index, { name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="e.g., Small, Medium, Large"
                            />
                          </div>
                          <div className="w-32">
                            <input
                              type="number"
                              value={variant.price}
                              onChange={(e) => updateWardrobeVariant(index, { price: Number(e.target.value) })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="Price"
                              min="1"
                            />
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={variant.isActive}
                                onChange={(e) => updateWardrobeVariant(index, { isActive: e.target.checked })}
                                className="accent-primary-600"
                              />
                              Active
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeWardrobeVariant(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      
                      {wardrobeVariants.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No wardrobe variants added yet. Click "Add Size" to get started.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Regular Variants (Modified to show conflict warning) */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Regular Variants</h3>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enableVariants}
                    onChange={(e) => {
                      setEnableVariants(e.target.checked)
                      if (e.target.checked && enableWardrobeVariants) {
                        setEnableWardrobeVariants(false)
                        toast("Wardrobe variants disabled - only one variant type allowed")
                      }
                    }}
                    className="accent-primary-600"
                  />
                  Enable Regular Variants
                </label>
              </div>
              {enableWardrobeVariants && (
                <p className="text-sm text-orange-600 mt-2">
                  ⚠️ Wardrobe variants are enabled. Only one variant type can be active at a time.
                </p>
              )}

              {enableVariants && (
                <div className="mt-4 space-y-6">
                  {/* Sizes */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">Sizes</h4>
                      <button
                        type="button"
                        onClick={addSize}
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        Add Size
                      </button>
                    </div>
                    <div className="space-y-2">
                      {variantSizes.map((s, i) => (
                        <div key={i} className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={s.label}
                              onChange={(e) => updateSizeLabel(i, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="e.g., Small, Medium, Large, XL, Custom"
                            />
                            <button
                              type="button"
                              onClick={() => removeSize(i)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="mt-2">
                            <label className="inline-flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={Boolean(s.dimensions)}
                                onChange={() => toggleCustomDimensions(i)}
                              />
                              Custom dimensions
                            </label>
                            {s.dimensions && (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                                {(["length", "width", "height"] as const).map((k) => (
                                  <input
                                    key={k}
                                    type="number"
                                    min={0}
                                    step={0.1}
                                    value={(s.dimensions as any)[k] ?? ""}
                                    onChange={(e) =>
                                      updateSizeDimension(i, k, e.target.value ? Number(e.target.value) : undefined)
                                    }
                                    className="px-3 py-2 border rounded-lg"
                                    placeholder={`${k[0].toUpperCase() + k.slice(1)} (in)`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Colors */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">Colors</h4>
                      <button
                        type="button"
                        onClick={addColor}
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        Add Color
                      </button>
                    </div>
                    <div className="space-y-4">
                      {variantColors.map((c, i) => (
                        <div key={i} className="p-3 border rounded-lg">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Color Name</label>
                              <input
                                type="text"
                                value={c.name}
                                onChange={(e) => updateColor(i, { name: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                                placeholder="e.g., Walnut, Teak, Black"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Hex</label>
                              <input
                                type="text"
                                value={c.hex}
                                onChange={(e) => updateColor(i, { hex: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                                placeholder="#AABBCC"
                              />
                            </div>
                            <div className="flex items-end justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Preview</span>
                                <span
                                  className="inline-block w-6 h-6 rounded-full border"
                                  style={{ backgroundColor: c.hex || "#ccc" }}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeColor(i)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Color image URLs via link */}
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">Images for {c.name || "color"}</span>
                            </div>

                            {/* Add new URL input */}
                            <div className="flex items-center gap-2">
                              <input
                                type="url"
                                value={colorImageInputs[i] || ""}
                                onChange={(e) => setColorImageInputs((prev) => ({ ...prev, [i]: e.target.value }))}
                                className="flex-1 px-3 py-2 border rounded-lg"
                                placeholder="https://example.com/image.jpg"
                              />
                              <button
                                type="button"
                                onClick={() => addColorImageUrl(i)}
                                className="px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800"
                              >
                                Add
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Paste a direct https image URL. These images are specific to this color.
                            </p>

                            {/* Existing URLs list */}
                            <div className="space-y-2 mt-3">
                              {(c.images || []).length === 0 && (
                                <p className="text-xs text-gray-500">No color-specific images yet.</p>
                              )}
                              {(c.images || []).map((url, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <input
                                    type="url"
                                    value={url}
                                    onChange={(e) => updateColorImageUrl(i, idx, e.target.value)}
                                    className="flex-1 px-3 py-2 border rounded-lg"
                                  />
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-2 py-2 text-xs rounded-lg border hover:bg-gray-50"
                                  >
                                    Preview
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => removeColorImageUrl(i, idx)}
                                    className="text-red-600 hover:text-red-700"
                                    aria-label="Remove color image URL"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Variant Matrix */}
                  <div>
                    <h4 className="text-lg font-semibold mb-4">Variant Matrix</h4>
                    <div className="space-y-4">
                      {variants.map((v) => (
                        <div key={v.id} className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 border rounded-lg">
                          <div className="text-sm">
                            <div className="font-medium">Size</div>
                            <div>{v.size}</div>
                          </div>
                          <div className="text-sm">
                            <div className="font-medium">Color</div>
                            <div className="inline-flex items-center gap-2">
                              <span
                                className="inline-block w-3 h-3 rounded-full border"
                                style={{ backgroundColor: v.colorHex || "#ccc" }}
                              />
                              <span>{v.color}</span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Price Modifier (₹)</label>
                            <input
                              type="number"
                              value={v.priceModifier || 0}
                              onChange={(e) => updateVariantField(v.id, "priceModifier", Number(e.target.value))}
                              className="w-full px-2 py-1 border rounded"
                            />
                            <div className="text-xs text-gray-600 mt-1">
                              Final: ₹{((watch("price") || 0) + (v.priceModifier || 0)).toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Stock</label>
                            <input
                              type="number"
                              min={0}
                              value={v.stock || 0}
                              onChange={(e) => updateVariantField(v.id, "stock", Number(e.target.value))}
                              className="w-full px-2 py-1 border rounded"
                            />
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={v.isActive !== false}
                                onChange={(e) => updateVariantField(v.id, "isActive", e.target.checked)}
                                className="mr-2"
                              />
                              <span className="text-sm">Active</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Images */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Images</h3>

              <div className="space-y-4">
                {/* Current Images */}
                {images.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Images</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {images.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={image || "/placeholder.svg"}
                            alt={`Image ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add image by URL */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Add image by URL</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="https://example.com/image.jpg"
                    />
                    <button
                      type="button"
                      onClick={handleAddImageUrl}
                      className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800"
                    >
                      Add
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Enter a direct https image URL. Max 5 images per product.</p>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Status</h3>
              <div className="space-y-4">
                <label className="flex items-center">
                  <input {...register("isFeatured")} type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-700">Featured Product</span>
                </label>
                <label className="flex items-center">
                  <input {...register("isNewArrival")} type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-700">New Arrival</span>
                </label>
                <label className="flex items-center">
                  <input {...register("isBestSeller")} type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-700">Best Seller</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? <LoadingSpinner size="sm" /> : "Update Product"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/admin/products")}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default EditProduct
